package com.rainbowclubs.rainbow.navigation

import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.rememberNavController
import androidx.navigation.toRoute
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.presentation.admin.*
import com.rainbowclubs.rainbow.presentation.club.ClubScreen
import com.rainbowclubs.rainbow.presentation.club.ClubViewModel
import com.rainbowclubs.rainbow.presentation.events.EventDetailsScreen
import com.rainbowclubs.rainbow.presentation.events.EventDetailsViewModel
import com.rainbowclubs.rainbow.presentation.home.HomeScreen
import com.rainbowclubs.rainbow.presentation.home.HomeViewModel
import com.rainbowclubs.rainbow.presentation.login.LoginScreen
import com.rainbowclubs.rainbow.presentation.login.SignupScreen
import com.rainbowclubs.rainbow.presentation.login.SignupViewModel
import com.rainbowclubs.rainbow.presentation.membership.MembershipDataScreen
import com.rainbowclubs.rainbow.presentation.membership.MembershipDataViewModel
import com.rainbowclubs.rainbow.presentation.messages.MessagesScreen
import com.rainbowclubs.rainbow.presentation.more.MoreScreen
import com.rainbowclubs.rainbow.presentation.more.MoreViewModel
import com.rainbowclubs.rainbow.presentation.select_club.SelectClubScreen
import com.rainbowclubs.rainbow.presentation.select_club.SelectClubViewModel
import com.rainbowclubs.rainbow.presentation.splash.LandingScreen
import com.rainbowclubs.rainbow.presentation.splash.LandingScreenNavigation
import com.rainbowclubs.rainbow.presentation.splash.SplashNavigationEvent
import com.rainbowclubs.rainbow.presentation.splash.SplashScreen
import org.koin.compose.KoinContext

@Composable
fun RainbowNavigation(
    navController: NavHostController = rememberNavController(),
) {

    Scaffold(containerColor = Color.Transparent) { innerPadding ->
        KoinContext {
            NavHost(
                navController = navController,
                startDestination = Screen.Splash.name,
            ) {

                composable(Screen.Splash.name) {
                    SplashScreen(
                        onLoadingFinished = { navigationEvent ->
                            when (navigationEvent) {
                                SplashNavigationEvent.Landing -> {
                                    navController.navigate(Screen.Landing.name) {
                                        popUpTo(Screen.Splash.name) { inclusive = true }
                                    }
                                }

                                is SplashNavigationEvent.Home -> {
                                    navController.navigate(HomeRoute(navigationEvent.clubId)) {
                                        popUpTo(Screen.Splash.name) { inclusive = true }
                                    }
                                }

                                SplashNavigationEvent.SelectClub -> {
                                    navController.navigate(Screen.SelectClub.name) {
                                        popUpTo(Screen.Splash.name) { inclusive = true }
                                    }
                                }
                            }
                        }
                    )
                }

                composable(Screen.Landing.name) {
                    LandingScreen(
                        onNavigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                LandingScreenNavigation.Login -> {
                                    navController.navigate(Screen.Login.name)
                                }

                                LandingScreenNavigation.Signup -> {
                                    navController.navigate(Screen.Signup.name)
                                }
                            }
                        }
                    )
                }

                composable(Screen.Signup.name) {
                    SignupScreen(
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                SignupViewModel.SignupNavigationEvent.SelectClub -> {
                                    navController.navigate(Screen.SelectClub.name)
                                }
                            }
                        }
                    )
                }

                composable(Screen.Login.name) {
                    LoginScreen(
                        navigateToHome = { clubId ->
                            navController.navigate(HomeRoute(clubId)) {
                                popUpTo(Screen.Splash.name) { inclusive = true }
                            }
                        },
                        navigateToSelectClub = {
                            navController.navigate(Screen.SelectClub.name)
                        },
                    )
                }

                composable<HomeRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<HomeRoute>()
                    HomeScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                is HomeViewModel.HomeNavigationEvent.Admin -> {
                                    navController.navigate(AdminRoute(navigationEvent.clubId))
                                }

                                is HomeViewModel.HomeNavigationEvent.Club -> {
                                    navController.navigate(ClubRoute(navigationEvent.clubId))
                                }

                                is HomeViewModel.HomeNavigationEvent.Messages -> {
                                    navController.navigate(MessagesRoute(navigationEvent.clubId))
                                }

                                is HomeViewModel.HomeNavigationEvent.More -> {
                                    navController.navigate(MoreRoute(navigationEvent.clubId))
                                }

                                is HomeViewModel.HomeNavigationEvent.EventDetails -> {
                                    navController.navigate(
                                        EventDetailsRoute(
                                            clubId = navigationEvent.clubId,
                                            eventId = navigationEvent.eventId,
                                        )
                                    )
                                }
                            }
                        }
                    )
                }

                composable<EventDetailsRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<EventDetailsRoute>()
                    EventDetailsScreen(
                        eventId = args.eventId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                EventDetailsViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }
                            }
                        },
                    )
                }

                composable<AdminRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<AdminRoute>()
                    AdminScreen(
                        clubId = args.clubId,
                        onBackClick = {
                            navController.popBackStack()
                        },
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                is AdminViewModel.AdminNavigationEvent.ManageEvents -> {
                                    navController.navigate(ManageEventsRoute(navigationEvent.clubId))
                                }

                                is AdminViewModel.AdminNavigationEvent.ManageClub -> {
                                    navController.navigate(ManageClubRoute(navigationEvent.clubId))
                                }

                                is AdminViewModel.AdminNavigationEvent.ManageMembers -> {
                                    navController.navigate(
                                        ManageMembersRoute(
                                            clubId = navigationEvent.clubId,
                                            role = navigationEvent.role.name,
                                        )
                                    )
                                }

                                is AdminViewModel.AdminNavigationEvent.TakeAttendance -> {
                                    navController.navigate(
                                        TakeAttendanceRoute(
                                            clubId = navigationEvent.clubId,
                                            eventId = navigationEvent.eventId,
                                        )
                                    )
                                }
                            }
                        }
                    )
                }

                composable<TakeAttendanceRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<TakeAttendanceRoute>()
                    TakeAttendanceScreen(
                        clubId = args.clubId,
                        eventId = args.eventId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                TakeAttendanceViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }
                            }
                        },
                    )
                }

                composable<ManageClubRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<ManageClubRoute>()
                    ManageClubScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                ManageClubViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }
                            }
                        },
                    )
                }

                composable<ManageMembersRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<ManageMembersRoute>()
                    ManageMembersScreen(
                        clubId = args.clubId,
                        role = args.role.toUserRole(),
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                ManageMembersViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }

                                is ManageMembersViewModel.NavigationEvent.NewInvitations -> {
                                    navController.navigate(PendingInvitationsRoute(navigationEvent.clubId))
                                }

                                is ManageMembersViewModel.NavigationEvent.ExistingMembers -> {
                                    navController.navigate(ExistingMembersRoute(navigationEvent.clubId))
                                }
                            }
                        },
                    )
                }

                composable<ExistingMembersRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<ExistingMembersRoute>()
                    ExistingMembersScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                ExistingMembersViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }

                                is ExistingMembersViewModel.NavigationEvent.MemberDetails -> {
                                    navController.navigate(
                                        ExistingMemberDetailsRoute(
                                            clubId = navigationEvent.clubId,
                                            membershipId = navigationEvent.membershipId,
                                        )
                                    )
                                }
                            }
                        },
                    )
                }

                composable<ExistingMemberDetailsRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<ExistingMemberDetailsRoute>()
                    ExistingMemberDetailsScreen(
                        clubId = args.clubId,
                        membershipId = args.membershipId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                ExistingMemberDetailsViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }
                            }
                        },
                    )
                }

                composable<PendingInvitationsRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<PendingInvitationsRoute>()
                    PendingInvitationsScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                PendingInvitationsViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }
                            }
                        },
                    )
                }

                composable<ManageEventsRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<ManageEventsRoute>()
                    ManageEventsScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                ManageEventsNavigationEvent.Back -> {
                                    navController.popBackStack()
                                }

                                is ManageEventsNavigationEvent.EditExistingEvent -> {
                                    navController.navigate(EditEventRoute(navigationEvent.clubId))
                                }

                                is ManageEventsNavigationEvent.CreateNewEvent -> {
                                    navController.navigate(CreateEventRoute(navigationEvent.clubId))
                                }
                            }
                        },
                    )
                }

                composable<CreateEventRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<CreateEventRoute>()
                    CreateEventScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                CreateEventViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }
                            }
                        },
                    )
                }

                composable<EditEventRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<EditEventRoute>()
                    EditEventScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                EditEventViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }

                                is EditEventViewModel.NavigationEvent.UpdateEvent -> {
                                    navController.navigate(
                                        UpdateEventRoute(
                                            clubId = navigationEvent.clubId,
                                            eventId = navigationEvent.eventId,
                                        )
                                    )
                                }
                            }
                        },
                    )
                }

                composable<UpdateEventRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<UpdateEventRoute>()
                    UpdateEventScreen(
                        clubId = args.clubId,
                        eventId = args.eventId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                UpdateEventViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }

                                UpdateEventViewModel.NavigationEvent.EventDeleted -> {
                                    navController.navigate(EditEventRoute(args.clubId)) {
                                        popUpTo(EditEventRoute(args.clubId)) {
                                            inclusive = true
                                        }
                                    }
                                }
                            }
                        },
                    )
                }

                composable<ClubRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<ClubRoute>()
                    ClubScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                is ClubViewModel.ClubNavigationEvent.Home -> {
                                    navController.navigate(HomeRoute(navigationEvent.clubId)) {
                                        popUpTo(HomeRoute(navigationEvent.clubId)) {
                                            inclusive = true
                                        }
                                    }
                                }

                                is ClubViewModel.ClubNavigationEvent.Messages -> {
                                    navController.navigate(MessagesRoute(navigationEvent.clubId))
                                }

                                is ClubViewModel.ClubNavigationEvent.More -> {
                                    navController.navigate(MoreRoute(navigationEvent.clubId))
                                }
                            }
                        }
                    )
                }

                composable<MessagesRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<MessagesRoute>()
                    MessagesScreen(
                        clubId = args.clubId,
                        onNavigateToHome = { clubId ->
                            navController.navigate(HomeRoute(clubId)) {
                                popUpTo(HomeRoute(clubId)) { inclusive = true }
                            }
                        },
                        onNavigateToClub = { clubId ->
                            navController.navigate(ClubRoute(clubId))
                        },
                        onNavigateToMore = { clubId ->
                            navController.navigate(MoreRoute(clubId))
                        }
                    )
                }

                composable<MoreRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<MoreRoute>()
                    MoreScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                is MoreViewModel.MoreNavigationEvent.Home -> {
                                    navController.navigate(HomeRoute(navigationEvent.clubId)) {
                                        popUpTo(HomeRoute(navigationEvent.clubId)) { inclusive = true }
                                    }
                                }

                                is MoreViewModel.MoreNavigationEvent.Club -> {
                                    navController.navigate(ClubRoute(navigationEvent.clubId))
                                }

                                is MoreViewModel.MoreNavigationEvent.Messages -> {
                                    navController.navigate(MessagesRoute(navigationEvent.clubId))
                                }

                                MoreViewModel.MoreNavigationEvent.SelectClub -> {
                                    navController.navigate(Screen.SelectClub.name)
                                }

                                MoreViewModel.MoreNavigationEvent.Landing -> {
                                    navController.navigate(Screen.Landing.name) {
                                        popUpTo(HomeRoute(args.clubId)) { inclusive = true }
                                    }
                                }
                            }
                        }
                    )
                }

                composable(Screen.SelectClub.name) {
                    SelectClubScreen(
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                SelectClubViewModel.NavigationEvent.Back -> {
                                    navController.popBackStack()
                                }

                                is SelectClubViewModel.NavigationEvent.Home -> {
                                    navController.navigate(HomeRoute(navigationEvent.clubId)) {
                                        popUpTo(Screen.Splash.name) { inclusive = true }
                                    }
                                }

                                is SelectClubViewModel.NavigationEvent.MembershipData -> {
                                    navController.navigate(MembershipDataRoute(navigationEvent.clubId))
                                }

                            }
                        }
                    )
                }

                composable<MembershipDataRoute> { backStackEntry ->
                    val args = backStackEntry.toRoute<MembershipDataRoute>()

                    MembershipDataScreen(
                        clubId = args.clubId,
                        navigateTo = { navigationEvent ->
                            when (navigationEvent) {
                                is MembershipDataViewModel.NavigationEvent.Home -> {
                                    navController.navigate(HomeRoute(navigationEvent.clubId)) {
                                        popUpTo(Screen.Splash.name) { inclusive = true }
                                    }
                                }
                            }
                        }
                    )
                }
            }
        }
    }
}

private fun String.toUserRole(): UserRole {
    return try {
        UserRole.valueOf(this)
    } catch (_: Exception) {
        UserRole.MEMBER
    }
}
